import { gql } from "@apollo/client";
import {
  INTERVIEW_GUIDE_FIELDS,
  INTERVIEW_GUIDE_HEADER_FIELDS,
} from "./fragments";

// ─── Queries ────────────────────────────────────────────────────────────

export const GET_INTERVIEW_GUIDES = gql`
  ${INTERVIEW_GUIDE_HEADER_FIELDS}
  query GetInterviewGuides(
    $filter: InterviewGuideFilter
    $pagination: Pagination
  ) {
    interviewGuides(filter: $filter, pagination: $pagination) {
      items {
        ...InterviewGuideHeaderFields
      }
      pageInfo {
        page
        pageSize
        totalItems
        totalPages
      }
    }
  }
`;

export const GET_INTERVIEW_GUIDE = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  query GetInterviewGuide($id: ID!) {
    interviewGuide(id: $id) {
      ...InterviewGuideFields
    }
  }
`;

// ─── Guide-level mutations ──────────────────────────────────────────────

export const CREATE_INTERVIEW_GUIDE = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation CreateInterviewGuide($input: InterviewGuideCreateInput!) {
    interviewGuideCreate(input: $input) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const GENERATE_AI_GUIDE = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation GenerateAIGuide($input: AIGuideGenerationInput!) {
    interviewGuideGenerateAI(input: $input) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const GENERATE_GUIDE_QUESTIONS = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation GenerateGuideQuestions(
    $guideId: ID!
    $input: AIQuestionsGenerationInput!
  ) {
    interviewGuideGenerateQuestions(guideId: $guideId, input: $input) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const GENERATE_DRAFT_QUESTION = gql`
  mutation GenerateDraftQuestion($input: GenerateDraftInput!) {
    interviewGuideGenerateDraft(input: $input) {
      text
      scoringGuide {
        level1
        level2
        level3
        level4
        level5
      }
    }
  }
`;

export const REFINE_GUIDE_QUESTION = gql`
  mutation RefineGuideQuestion($input: RefineQuestionInput!) {
    interviewGuideRefineQuestion(input: $input) {
      text
      scoringGuide {
        level1
        level2
        level3
        level4
        level5
      }
    }
  }
`;

export const UPDATE_INTERVIEW_GUIDE = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation UpdateInterviewGuide($id: ID!, $input: InterviewGuideUpdateInput!) {
    interviewGuideUpdate(id: $id, input: $input) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const ARCHIVE_INTERVIEW_GUIDE = gql`
  ${INTERVIEW_GUIDE_HEADER_FIELDS}
  mutation ArchiveInterviewGuide($id: ID!) {
    interviewGuideArchive(id: $id) {
      interviewGuide {
        ...InterviewGuideHeaderFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const RESTORE_INTERVIEW_GUIDE = gql`
  ${INTERVIEW_GUIDE_HEADER_FIELDS}
  mutation RestoreInterviewGuide($id: ID!) {
    interviewGuideRestore(id: $id) {
      interviewGuide {
        ...InterviewGuideHeaderFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const DELETE_INTERVIEW_GUIDE = gql`
  mutation DeleteInterviewGuide($id: ID!) {
    interviewGuideDelete(id: $id) {
      interviewGuide {
        id
      }
      errors {
        message
        code
      }
    }
  }
`;

export const DUPLICATE_INTERVIEW_GUIDE = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation DuplicateInterviewGuide($id: ID!) {
    interviewGuideDuplicate(id: $id) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const REQUEST_INPUT = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation RequestInput($id: ID!, $input: RequestInputInput!) {
    interviewGuideRequestInput(id: $id, input: $input) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const REMOVE_INPUT_REQUEST = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation RemoveInputRequest($id: ID!, $userId: ID!) {
    interviewGuideRemoveInputRequest(id: $id, userId: $userId) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

// ─── Batch save (the editor's held-until-save model) ────────────────────

export const SAVE_INTERVIEW_GUIDE = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation SaveInterviewGuide(
    $id: ID!
    $input: InterviewGuideSaveInput!
    $publish: Boolean
  ) {
    interviewGuideSave(id: $id, input: $input, publish: $publish) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

// Non-persisting: generate questions from a PD and return them for the editor
// to hold in local state (the eventual SAVE_INTERVIEW_GUIDE writes them).
export const GENERATE_GUIDE_QUESTIONS_PREVIEW = gql`
  mutation GenerateGuideQuestionsPreview(
    $guideId: ID!
    $input: AIQuestionsGenerationInput!
  ) {
    interviewGuideGenerateQuestionsPreview(guideId: $guideId, input: $input) {
      text
      scoringGuide {
        level1
        level2
        level3
        level4
        level5
      }
      kssartiTopic
    }
  }
`;

// ─── Question-level mutations (within a guide) ──────────────────────────

export const ADD_GUIDE_QUESTION = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation AddGuideQuestion($guideId: ID!, $input: GuideQuestionInput!) {
    interviewGuideQuestionAdd(guideId: $guideId, input: $input) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const UPDATE_GUIDE_QUESTION = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation UpdateGuideQuestion(
    $guideId: ID!
    $questionId: ID!
    $input: GuideQuestionUpdateInput!
  ) {
    interviewGuideQuestionUpdate(
      guideId: $guideId
      questionId: $questionId
      input: $input
    ) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const REMOVE_GUIDE_QUESTION = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation RemoveGuideQuestion($guideId: ID!, $questionId: ID!) {
    interviewGuideQuestionRemove(guideId: $guideId, questionId: $questionId) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const REORDER_GUIDE_QUESTIONS = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation ReorderGuideQuestions($guideId: ID!, $questionIds: [ID!]!) {
    interviewGuideQuestionsReorder(
      guideId: $guideId
      questionIds: $questionIds
    ) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const LOCK_GUIDE_QUESTION = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation LockGuideQuestion(
    $guideId: ID!
    $questionId: ID!
    $locked: Boolean!
  ) {
    interviewGuideQuestionLock(
      guideId: $guideId
      questionId: $questionId
      locked: $locked
    ) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;

export const SAVE_QUESTION_TO_BANK = gql`
  ${INTERVIEW_GUIDE_FIELDS}
  mutation SaveQuestionToBank(
    $guideId: ID!
    $questionId: ID!
    $input: SaveToBankInput
  ) {
    interviewGuideQuestionSaveToBank(
      guideId: $guideId
      questionId: $questionId
      input: $input
    ) {
      interviewGuide {
        ...InterviewGuideFields
      }
      errors {
        message
        code
      }
    }
  }
`;
